/**
 * GENERATED by /docs-writer from docs/securelink-device-provision.md — DO NOT EDIT BY HAND.
 * Edit the markdown and run `/docs-writer update docs/securelink-device-provision.md`.
 *
 * source-sha: 353e1ff58a90e53ba8bad6cde785e35f80f4e463
 * sources (from the markdown's "Sources" section):
 *   - Conversation with Binh, 2026-10-28: region priority, SecureLink Redis rule, key handling, go-live
 *     measurements, sandbox test.
 *   - mdm-api main at 667cf66b: src/services/securelinkRegion.ts, src/services/ipGeolocation.ts,
 *     src/services/orchestratorClient.ts, src/services/securelinkService.ts,
 *     src/routes/securelink.ts, src/common/securelink-region.constants.ts.
 *   - mdm-api MRs !79, !82 (main) and !80, !81, !83 (develop); infras tasks 474 and 376.
 *   - docs/securelink.md, docs/securelink-tcp-relay.md in this repo.
 */
import { Link } from 'react-router';
import { DocSequence } from '../components/DocSequence';
import { DocsToc } from '../components/DocsToc';

const TOC = [
  { id: 'what-happens-in-one-provision', label: 'What happens in one provision' },
  { id: 'how-the-device-api-picks-the-hub', label: 'How the device API picks the hub' },
  { id: 'what-the-ip-lookup-costs', label: 'What the IP lookup costs' },
  { id: 'when-a-device-changes-hub', label: 'When a device changes hub' },
  { id: 'where-to-look', label: 'Where to look' },
];

export function SecurelinkDeviceProvisionDocsPage() {
  return (
    <>
      <div className="page doc" id="top">
        <p className="doc-eyebrow">Network · Reference</p>
        <h1 className="doc-title">How a signage device provisions SecureLink</h1>
        <p className="doc-lede">
          A signage device joins SecureLink with one signed request to the device API. The device API checks the
          request, picks a regional hub from the <strong>country of the device&rsquo;s public IP</strong>, and asks
          that hub&rsquo;s wg-orchestrator for a peer. The device gets back its overlay address, the hub&rsquo;s
          endpoint and the hub&rsquo;s public key, and brings the tunnel up. This page follows that request end to
          end and says where to look when a device lands on the wrong hub or does not connect.
        </p>
        <p className="doc-meta">
          Updated <time dateTime="2026-10-28">28 Oct 2026</time>
        </p>

        <h2 className="doc-h2" id="what-happens-in-one-provision">What happens in one provision</h2>
        <p className="doc-p">
          The device API decides the hub before it talks to any hub. The IP lookup is cached, so most provisions
          never leave the device API&rsquo;s own network for it.
        </p>
        <DocSequence
          caption="One provision, from the signed request to the WireGuard handshake (as of 28 Oct 2026)"
          participants={[
            { id: 'Dev', label: 'Signage device', note: 'Android Util or Pro Util' },
            { id: 'Api', label: 'Device API' },
            { id: 'Cache', label: 'Lookup cache', note: 'SecureLink Redis' },
            { id: 'Geo', label: 'ipgeolocation.io' },
            { id: 'Hub', label: 'wg-orchestrator', note: 'on the chosen hub' },
          ]}
          steps={[
            { from: 'Dev', to: 'Api', text: 'Signed provision: UUID, public key' },
            { from: 'Api', to: 'Api', text: 'Check signature and kill switch' },
            { from: 'Api', to: 'Cache', text: 'Country of this IP?' },
            { kind: 'reply', from: 'Cache', to: 'Api', text: 'Country, or a miss' },
            { kind: 'note', over: ['Api', 'Geo'], text: 'Only on a miss' },
            { from: 'Api', to: 'Geo', text: 'Look up the IP' },
            { kind: 'reply', from: 'Geo', to: 'Api', text: 'Country code' },
            { from: 'Api', to: 'Cache', text: 'Keep it for 30 days' },
            { from: 'Api', to: 'Api', text: 'Pick the hub' },
            { from: 'Api', to: 'Hub', text: 'Create the peer' },
            { kind: 'reply', from: 'Hub', to: 'Api', text: 'Address, endpoint, hub key' },
            { kind: 'reply', from: 'Api', to: 'Dev', text: 'Tunnel settings' },
            { from: 'Dev', to: 'Hub', text: 'WireGuard handshake on UDP 443' },
          ]}
        />
        <ul className="doc-list">
          <li>
            <strong>The device&rsquo;s IP is real.</strong> Both production load balancers in front of the device API
            pass the client address through to it, so the API sees the device&rsquo;s public IP, not a load balancer.
          </li>
          <li>
            <strong>A signature proves the platform, not the device.</strong> Every Android device signs with the same
            platform key, so a valid request shows it came from an OptiSigns client. It does not prove the UUID
            belongs to the caller.
          </li>
          <li>
            <strong>The account kill switch refuses the request.</strong> A device whose account has SecureLink turned
            off gets 403 and no peer. A device that is not paired to any account still provisions.
          </li>
          <li>
            <strong>A busy hub answers 429.</strong> The device API passes the 429 and the hub&rsquo;s{' '}
            <code>Retry-After</code> on to the device.
          </li>
        </ul>

        <h2 className="doc-h2" id="how-the-device-api-picks-the-hub">How the device API picks the hub</h2>
        <p className="doc-p">
          The device API takes the first answer in this order. Each step runs only when every step above it gave no
          answer.
        </p>
        <div className="doc-table">
          <table>
            <thead>
              <tr>
                <th className="doc-num">Order</th>
                <th>Source</th>
                <th>Gives no answer when</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="doc-num">1</td>
                <td>Country of the device&rsquo;s public IP</td>
                <td>the lookup fails, times out or returns no country</td>
              </tr>
              <tr>
                <td className="doc-num">2</td>
                <td>Country on the device&rsquo;s record</td>
                <td>the record has no country, or it is not a real country code</td>
              </tr>
              <tr>
                <td className="doc-num">3</td>
                <td>Time zone on the device&rsquo;s record</td>
                <td>
                  the zone says nothing about place, such as <code>UTC</code> or <code>Asia/Shanghai</code>
                </td>
              </tr>
              <tr>
                <td className="doc-num">4</td>
                <td>The hub the device already uses</td>
                <td>the device has never provisioned</td>
              </tr>
              <tr>
                <td className="doc-num">5</td>
                <td>US</td>
                <td>never</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="doc-p">
          A country maps to a hub by a fixed table. Asia, the Middle East, Australia, the Pacific and the Indian Ocean
          go to <strong>APAC</strong>. Europe and Africa go to <strong>EU</strong>. Every other country, the Americas
          included, goes to <strong>US</strong>. Vietnam (<code>VN</code>) maps to APAC, for example.
        </p>
        <aside className="doc-note">
          <span className="doc-note-label">Note</span>
          <p>
            Today only the APAC hub is enabled for placement. When the IP points to Europe, the device stays on the
            hub it already uses, which is US for almost every device. On 28 Oct 2026 this kept 29 of the first 219
            decisions on US.
          </p>
        </aside>
        <p className="doc-p">
          A provision that carries the device&rsquo;s hardware serial skips this table. When another peer already
          holds that serial, for example after a factory reset gave the device a new UUID, the device takes over that
          peer&rsquo;s hub and keeps its address.
        </p>

        <h2 className="doc-h2" id="what-the-ip-lookup-costs">What the IP lookup costs</h2>
        <p className="doc-p">
          One lookup is one ipgeolocation.io credit. The cache keeps each answer, so a device behind an IP already
          seen costs nothing.
        </p>
        <ul className="doc-list">
          <li>
            <strong>The cache keeps a country for 30 days.</strong> An IP that the service answers without a country
            is kept for 1 day. A failed lookup is not kept, so the next provision tries again.
          </li>
          <li>
            <strong>A lookup never blocks a provision for long.</strong> The cache read gives up after 300 ms and the
            service call after 2 seconds. Either way the device API moves on to the next step in the table.
          </li>
          <li>
            <strong>The cache holds about 56 bytes an IP.</strong> 80,000 distinct IPs take about 4.5 MB.
          </li>
          <li>
            <strong>The lookup runs only while placement is on.</strong> With placement off, the device API keeps
            every device on the hub it already uses and spends no credits.
          </li>
        </ul>

        <h2 className="doc-h2" id="when-a-device-changes-hub">When a device changes hub</h2>
        <p className="doc-p">
          A device changes hub only when it provisions again. A reboot reconnects with the saved settings, so a device
          stays on its old hub until something makes it provision.
        </p>
        <p className="doc-p">
          A move gives the device <strong>a new overlay address</strong>, because a peer belongs to one hub. Anything
          that saved the old address, such as a support bookmark or a script, has to look it up again. The serial
          rule above is the one case where the address survives, because the device takes over an existing peer.
        </p>

        <h2 className="doc-h2" id="where-to-look">Where to look</h2>
        <div className="doc-table">
          <table>
            <thead>
              <tr>
                <th>You want to know</th>
                <th>Look at</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Which hub a device is on, and when it last completed a handshake</td>
                <td>
                  <Link to="/vpn/peers">Network → Peers</Link>, devices tab
                </td>
              </tr>
              <tr>
                <td>When a device provisioned, and what the device API answered</td>
                <td>The device&rsquo;s timeline on the Peers page</td>
              </tr>
              <tr>
                <td>Which peers connected to a device</td>
                <td>
                  <Link to="/vpn/connection-audit">Network → Connection audit</Link> (root only)
                </td>
              </tr>
              <tr>
                <td>Why the device API picked a hub</td>
                <td>The device API&rsquo;s log line for that provision, described below</td>
              </tr>
            </tbody>
          </table>
        </div>

        <aside className="doc-next">
          <div>
            <h2 className="doc-next-title">New to SecureLink?</h2>
            <p>Start with what it is made of and how each client connects.</p>
          </div>
          <Link className="btn" to="/docs/securelink">
            SecureLink
          </Link>
        </aside>

        <p className="doc-back">
          <a href="#top">Back to top ↑</a>
        </p>
      </div>
      <DocsToc items={TOC} />
    </>
  );
}
